import { yupResolver } from '@hookform/resolvers/yup';
import { AvailableCurrencyDropdownWithOptionalDefault } from '@src/clients/financialSettings/AvailableCurrencyDropdownWithOptionalDefault';
import { CurrencyField } from '@src/clients/financialSettings/fields/CurrencyFields';
import { BaseModal } from '@src/common/components/BaseModal';
import { Button } from '@src/common/components/button/Button';
import { Divider } from '@src/common/components/Divider';
import { Stack } from '@src/common/components/Stack';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import type { Transaction } from '@src/orders/domain/Transaction';
import { CreditCardField } from '@src/orders/fields/CreditCardRelatedFields';
import { ControllerFormNumberInput } from '@src/orders/PermitFormSections/ControllerFormNumberInput';
import { AmountField } from '@src/orders/review/AmountField';
import { useSettleTransaction } from '@src/orders/review/hooks/useSettleTransaction';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

interface Props {
  orderId: string;
  onClose: () => void;
  preauthorization: Transaction;
  clientId: string;
  maxBillable: number;
}

export const SettlePaymentModal = ({
  orderId,
  onClose,
  preauthorization,
  maxBillable,
}: Props) => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToast();

  const { settle } = useSettleTransaction({
    orderId,
    onSuccess: ({ amount }) => {
      onClose();
      showSuccessToast(t('settlementModal.success', { amount }));
    },
    onError: () => {
      onClose();
      showErrorToast(t('settlementModal.error'));
    },
  });

  const {
    control,
    getValues,
    setValue,
    formState: { isValid, isSubmitting },
    handleSubmit,
  } = useForm({
    resolver: yupResolver(
      yup
        .object()
        .shape({
          [AmountField.fieldName]: AmountField.validator(
            Math.min(preauthorization.amount, maxBillable),
          ),
          [CurrencyField.fieldName]: CurrencyField.validator,
          [CreditCardField.fieldName]: yup.string(),
        })
        .required(),
    ),
    mode: 'onChange',
    defaultValues: {
      [AmountField.fieldName]: preauthorization.amount,
    },
  });

  const submitSettlement = async () => {
    const amount = Number(getValues()[AmountField.fieldName]);
    if (!amount) {
      return;
    }
    try {
      await settle({
        transactionId: preauthorization.transactionId,
        amount,
      });
    } catch (_) {
      //Do nothing
    }
  };
  const canSubmit = !isSubmitting && isValid;

  return (
    <BaseModal
      title={t('settlementModal.title')}
      subtitle={t('settlementModal.subtitle')}
      open={true}
      onClose={onClose}
      marginBottom="0"
    >
      <Divider style={{ marginBottom: theme.spacing['9'] }} />
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(submitSettlement)(e);
        }}
      >
        <Stack gap={theme.spacing['6']}>
          <Stack gap={theme.spacing['6']} direction="row">
            <ControllerFormNumberInput
              control={control}
              fieldName={AmountField.fieldName}
              placeholder={t('settlementModal.enterAmount')}
            />
            <AvailableCurrencyDropdownWithOptionalDefault
              control={control}
              disabled={true}
              defaultCurrency={preauthorization.currency}
              validateOnAvailableDefaultCurrency={true}
              setValue={setValue}
            />
          </Stack>

          <Divider />

          <Stack direction="row" justifyContent="end" gap={theme.spacing['3']}>
            <Button onClick={onClose} variant="outlined">
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit} variant="contained">
              {t('settle')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </BaseModal>
  );
};
